import { lazy, Suspense, type ComponentType } from "react";
import type { RouteObject } from "react-router-dom";

import { RequireAuth } from "@/auth/RequireAuth";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import MobileFrame from "@/components/layout/MobileFrame";
import type { StoredUser } from "@/auth/storage";

// Login 不做懒加载 —— 未登录时第一个进入的页面，尽量让它立刻可见
import Login from "../pages/login/page";
// NotFound 很小，不做懒加载
import NotFound from "../pages/NotFound";

// 其余路由按需加载，每个页面产物单独成 chunk
const Home = lazy(() => import("../pages/home/page"));
const Tasks = lazy(() => import("../pages/tasks/page"));
const Recite = lazy(() => import("../pages/recite/page"));
const Reading = lazy(() => import("../pages/reading/page"));
const BookDetail = lazy(() => import("../pages/reading/[bookId]/page"));
const MaterialDetail = lazy(() => import("../pages/materials/page"));
const Profile = lazy(() => import("../pages/profile/page"));
const Chat = lazy(() => import("../pages/chat/page"));
const LegendShowcase = lazy(() => import("../pages/legend/[poetId]/page"));
// 二期桌面古风样张（原型，公开访问便于评审，不接后端）
const Proto = lazy(() => import("../pages/proto/page"));
const ProtoTasks = lazy(() => import("../pages/proto/tasks"));
const PreviewTasksLive = lazy(() => import("../pages/preview/tasks-live"));
const PreviewReciteModal = lazy(() => import("../pages/preview/recite-modal"));
const PreviewBookSummary = lazy(() => import("../pages/preview/book-summary"));
const ProtoRecite = lazy(() => import("../pages/proto/recite"));
const ProtoReading = lazy(() => import("../pages/proto/reading"));
const ProtoChat = lazy(() => import("../pages/proto/chat"));
const Admin = lazy(() => import("../pages/admin/page"));
const Teacher = lazy(() => import("../pages/teacher/page"));

// 路由 element 统一用 <Lazy /> 组件包裹 lazy 页面，走 Suspense 兜底
// eslint-disable-next-line react-refresh/only-export-components
function Lazy({ component: C, frame = false }: { component: ComponentType; frame?: boolean }) {
  const content = (
    <Suspense
      fallback={
        <div
          className="min-h-screen flex items-center justify-center"
          style={{ background: "linear-gradient(160deg, #F2F6F0 0%, #E8F0E4 100%)" }}
        >
          <LoadingSpinner size={32} />
        </div>
      }
    >
      <C />
    </Suspense>
  );
  // 学生端为移动优先：宽屏上约束为居中手机画幅；管理端/教师端为桌面全宽
  return frame ? <MobileFrame>{content}</MobileFrame> : content;
}

function ProtectedLazy({
  component,
  roles,
  frame = true,
}: {
  component: ComponentType;
  roles?: StoredUser["role"][];
  frame?: boolean;
}) {
  return (
    <RequireAuth roles={roles}>
      <Lazy component={component} frame={frame} />
    </RequireAuth>
  );
}

const routes: RouteObject[] = [
  { path: "/login", element: <Login /> },
  // 二期：首页改为自适应（页面内 lg 断点切换桌面/移动），不再套手机画幅
  { path: "/", element: <ProtectedLazy component={Home} frame={false} /> },
  { path: "/tasks", element: <ProtectedLazy component={Tasks} frame={false} /> },
  { path: "/recite", element: <ProtectedLazy component={Recite} frame={false} /> },
  { path: "/reading", element: <ProtectedLazy component={Reading} frame={false} /> },
  { path: "/reading/:bookId", element: <ProtectedLazy component={BookDetail} /> },
  { path: "/materials/:id", element: <ProtectedLazy component={MaterialDetail} /> },
  { path: "/profile", element: <ProtectedLazy component={Profile} frame={false} /> },
  { path: "/chat", element: <ProtectedLazy component={Chat} frame={false} /> },
  // 展示页 —— 公开访问，无需登录，便于对外演示
  { path: "/legend/:poetId", element: <Lazy component={LegendShowcase} frame /> },
  // 二期样张：桌面全宽（不套手机画幅），公开访问
  { path: "/proto", element: <Lazy component={Proto} frame={false} /> },
  // 二期预览：免登录直接看首页版式（未登录时数据请求关闭，仅占位空值，无报错）
  { path: "/preview/home", element: <Lazy component={Home} frame={false} /> },
  // 二期预览：学习任务桌面「叠放画廊」样张（mock 数据）
  { path: "/preview/tasks", element: <Lazy component={ProtoTasks} frame={false} /> },
  // 二期预览：真实 DesktopTasks 组件 + 内置演示数据（看 5 张扇形 / 右栏滚动 / 已完成折叠）
  { path: "/preview/tasks-live", element: <Lazy component={PreviewTasksLive} frame={false} /> },
  // 二期预览：背诵练习「信笺卡」弹窗样张（答题/结果切换，免登录）
  { path: "/preview/recite-modal", element: <Lazy component={PreviewReciteModal} frame={false} /> },
  // 二期预览：书籍概要面板（屏B）槽位对照
  { path: "/preview/book-summary", element: <Lazy component={PreviewBookSummary} frame={false} /> },
  { path: "/preview/recite", element: <Lazy component={ProtoRecite} frame={false} /> },
  { path: "/preview/reading", element: <Lazy component={ProtoReading} frame={false} /> },
  { path: "/preview/chat", element: <Lazy component={ProtoChat} frame={false} /> },
  // 免登录预览完整教师端外壳（带侧栏导航）；?nav=classroom-review 直达复习任务 Tab
  { path: "/preview/teacher", element: <Lazy component={Teacher} frame={false} /> },
  { path: "/preview/profile", element: <Lazy component={Profile} frame={false} /> },
  { path: "/admin", element: <ProtectedLazy component={Admin} roles={["admin"]} frame={false} /> },
  { path: "/teacher", element: <ProtectedLazy component={Teacher} roles={["teacher", "assistant", "admin"]} frame={false} /> },
  { path: "*", element: <NotFound /> },
];

export default routes;
